import { useCallback } from 'react';

/**
 * NAVIGATION PAR FLÈCHES — implémentation UNIQUE du projet.
 *
 * Trois motifs ARIA promettent les flèches à qui lit leur rôle : `menu` (↑/↓/Home/End),
 * `tablist` (←/→/Home/End) et `radiogroup` (les quatre flèches, avec bouclage). Le dépôt
 * en avait TROIS copies divergentes (`Menu.tsx`, `ToolsMenu.tsx`, `OverflowMenu.tsx`) plus
 * `useRovingTabs`, et AUCUNE sur les `role="radiogroup"` — 16 au total, dont 14 bâtis sur
 * des `<button role="radio">` (les 2 de `dedup/` reposent sur des radios NATIVES, que le
 * navigateur équipe déjà). Tout passe désormais par le noyau ci-dessous.
 *
 * DÉFAUT CORRIGÉ — les entrées DÉSACTIVÉES n'étaient pas enjambées. Les menus faisaient
 * `(i + 1) % actions.length` puis `items[i].focus()`. Or, dans un VRAI navigateur,
 * `focus()` sur un `<button disabled>` est un NO-OP : le focus restait sur l'entrée
 * précédente et la navigation par flèches était MORTE. Cas réel : pendant un scan,
 * l'entrée « Scan en cours… » est désactivée — le menu Outils était donc inutilisable au
 * clavier pendant toute la durée du scan.
 *
 * ⚠ happy-dom, lui, AUTORISE `focus()` sur un bouton désactivé. Un test qui se contente
 * d'appeler `focus()` puis de lire `document.activeElement` verdit donc à tort contre le
 * code fautif : les tests doivent asserter que le focus atterrit sur l'entrée ACTIVABLE
 * attendue (cf. `rovingFocus.test.tsx`).
 */

/** Axe des flèches acceptées. `both` = les quatre (motif `radiogroup`). */
export type RovingOrientation = 'vertical' | 'horizontal' | 'both';

const NEXT_KEYS: Record<RovingOrientation, readonly string[]> = {
  vertical: ['ArrowDown'],
  horizontal: ['ArrowRight'],
  both: ['ArrowDown', 'ArrowRight'],
};
const PREV_KEYS: Record<RovingOrientation, readonly string[]> = {
  vertical: ['ArrowUp'],
  horizontal: ['ArrowLeft'],
  both: ['ArrowUp', 'ArrowLeft'],
};

/** La touche fait-elle partie du jeu géré pour cette orientation ? */
export function isRovingKey(key: string, orientation: RovingOrientation): boolean {
  return (
    NEXT_KEYS[orientation].includes(key) ||
    PREV_KEYS[orientation].includes(key) ||
    key === 'Home' ||
    key === 'End'
  );
}

/**
 * Voisin ACTIVABLE de `from` dans le sens `delta`, avec bouclage.
 *
 * La boucle est bornée par le nombre d'entrées : un groupe ENTIÈREMENT désactivé rend
 * `null` au lieu de tourner indéfiniment. Si une seule entrée est activable, elle est
 * rendue telle quelle (`next === from`) — aux appelants d'en tirer les conséquences.
 */
function seek(enabled: readonly boolean[], from: number, delta: 1 | -1): number | null {
  const n = enabled.length;
  if (n === 0) return null;
  for (let k = 1; k <= n; k += 1) {
    const i = (((from + delta * k) % n) + n) % n;
    if (enabled[i] === true) return i;
  }
  return null;
}

/** Première (`+1`) ou dernière (`-1`) entrée activable ; `null` si aucune. */
function edge(enabled: readonly boolean[], delta: 1 | -1): number | null {
  const n = enabled.length;
  for (let k = 0; k < n; k += 1) {
    const i = delta === 1 ? k : n - 1 - k;
    if (enabled[i] === true) return i;
  }
  return null;
}

/** Première entrée activable, ou `0` à défaut (aucune n'est activable). */
export function firstEnabledIndex(enabled: readonly boolean[]): number {
  return edge(enabled, 1) ?? 0;
}

/**
 * Index cible d'une touche de navigation, en ENJAMBANT les entrées désactivées.
 *
 * Rend `null` si la touche n'est pas gérée OU si aucune entrée n'est activable — dans les
 * deux cas, l'appelant ne bouge pas.
 */
export function resolveRovingIndex(
  key: string,
  current: number,
  enabled: readonly boolean[],
  orientation: RovingOrientation,
): number | null {
  if (NEXT_KEYS[orientation].includes(key)) return seek(enabled, current, 1);
  if (PREV_KEYS[orientation].includes(key)) return seek(enabled, current, -1);
  if (key === 'Home') return edge(enabled, 1);
  if (key === 'End') return edge(enabled, -1);
  return null;
}

/**
 * `tabIndex` MOBILE : un seul point d'entrée dans l'ordre de tabulation par groupe.
 * Les flèches déplacent le focus À L'INTÉRIEUR, Tab entre et sort.
 */
export function rovingTabIndex(selected: boolean): 0 | -1 {
  return selected ? 0 : -1;
}

/**
 * `tabIndex` mobile d'un `radiogroup` où AUCUN radio n'est forcément coché (les trois
 * sélecteurs d'étoiles : la note 0 « non noté » n'a pas de radio). Le motif ARIA impose
 * malgré tout exactement un point d'entrée : à défaut de coché, c'est le PREMIER.
 */
export function rovingRadioTabIndex(
  checked: boolean,
  isFirst: boolean,
  anyChecked: boolean,
): 0 | -1 {
  return rovingTabIndex(checked || (!anyChecked && isFirst));
}

/** Une entrée est activable si elle n'est ni `disabled` ni `aria-disabled="true"`. */
function isEnabled(el: HTMLElement): boolean {
  return !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true';
}

export interface RovingFocusOptions {
  /** Sélecteur CSS des entrées, cherché SOUS le conteneur portant le gestionnaire. */
  itemSelector: string;
  orientation: RovingOrientation;
  /**
   * `manual` : les flèches DÉPLACENT le focus, Entrée/Espace activent (clic natif du
   * `<button>`). À réserver aux groupes dont l'activation COÛTE : requête réseau,
   * écriture en base, navigation.
   *
   * `auto` : la SÉLECTION SUIT LE FOCUS — c'est le motif `radiogroup` standard. L'entrée
   * atteinte est cliquée, donc son `onClick` habituel s'exécute (aucun câblage en plus).
   */
  activate: 'auto' | 'manual';
}

/**
 * Gestionnaire `onKeyDown` à poser sur le CONTENEUR d'un groupe (`role="menu"`,
 * `role="tablist"`, `role="radiogroup"`) dont les entrées vivent dans le DOM.
 *
 * Les menus, eux, pilotent le focus par un `activeIndex` d'état : ils appellent
 * directement `resolveRovingIndex` (même noyau, autre plomberie).
 */
export function useRovingFocus({
  itemSelector,
  orientation,
  activate,
}: RovingFocusOptions): (e: React.KeyboardEvent<HTMLElement>) => void {
  return useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (!isRovingKey(e.key, orientation)) return;

      const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(itemSelector));
      if (items.length === 0) return;
      // Focus hors du groupe (ou sur un descendant) : rien à déplacer.
      const current = items.indexOf(document.activeElement as HTMLElement);
      if (current < 0) return;

      const next = resolveRovingIndex(e.key, current, items.map(isEnabled), orientation);
      if (next === null) return; // groupe entièrement désactivé
      const target = items[next];
      if (target === undefined) return;

      e.preventDefault();
      target.focus();
      // `next === current` quand une SEULE entrée est activable : ne pas la re-cliquer,
      // plusieurs de ces `onClick` sont des BASCULES (re-cliquer l'étoile courante remet
      // la note à zéro) — une flèche ne doit jamais décocher.
      if (activate === 'auto' && next !== current) target.click();
    },
    [itemSelector, orientation, activate],
  );
}

/**
 * Navigation clavier d'un `role="radiogroup"` de `<button role="radio">`.
 *
 * Les radios NATIVES (`<input type="radio">` d'un même `name`) n'en ont pas besoin : le
 * navigateur leur fournit déjà flèches et `tabIndex` mobile. C'est pourquoi les deux
 * groupes de `dedup/` sont laissés tels quels.
 */
export function useRovingRadioGroup(
  activate: 'auto' | 'manual',
): (e: React.KeyboardEvent<HTMLElement>) => void {
  return useRovingFocus({ itemSelector: '[role="radio"]', orientation: 'both', activate });
}
